// Map.jsx - Fixed version with Custom Overlay for user location marker
// This eliminates React reconciliation flickering during manual panning

import React, { useState, useEffect, useRef, useCallback } from 'react';
import GoogleMapReact from 'google-map-react';
import moment from 'moment';
import 'moment/locale/th';
import 'moment/locale/zh-cn';

import mapStyles from './mapStyles';
import { groupNearbyEvents, setMomentLocale } from './MapUtils';
import { createCustomOverlayClass } from './MapOverlay';
import { createUserLocationOverlayClass } from './UserLocationOverlay';

// Helper function for deep bounds equality check with tolerance
const areBoundsEqual = (bounds1, bounds2, tolerance = 0.0001) => {
  if (!bounds1 || !bounds2) return false;
  if (!bounds1.ne || !bounds1.sw || !bounds2.ne || !bounds2.sw) return false;

  return (
    Math.abs(bounds1.ne.lat - bounds2.ne.lat) < tolerance &&
    Math.abs(bounds1.ne.lng - bounds2.ne.lng) < tolerance &&
    Math.abs(bounds1.sw.lat - bounds2.sw.lat) < tolerance &&
    Math.abs(bounds1.sw.lng - bounds2.sw.lng) < tolerance
  );
};

const Map = ({
  coordinates,
  places,
  setCoordinates,
  setBounds,
  bounds: propBounds,
  selectedEventId,
  onMarkerClick,
  language = 'TH',
  userLocation: propUserLocation,
  onSearch,
  controlsBottomOffset = null,
  disablePopups = false,
  onInfoBoxClick,
  onCreatorClick,
}) => {
  const [mapsReady, setMapsReady] = useState(!!(window.google && window.google.maps));
  const [mapInstance, setMapInstance] = useState(null);
  const [mapApi, setMapApi] = useState(null);
  const overlaysRef = useRef({});
  const CustomOverlayClassRef = useRef(null);
  const [groupedPlaces, setGroupedPlaces] = useState([]);
  const initialBoundsSetRef = useRef(false);
  const lastBoundsRef = useRef(null);
  const [loadingLocation, setLoadingLocation] = useState(false);
  const [loadingCurrentLocation, setLoadingCurrentLocation] = useState(false);
  const [, setLocationPermission] = useState('prompt');
  const [localUserLocation, setLocalUserLocation] = useState(null);

  // CRITICAL FIX: Use Custom Overlay for user location instead of React component
  const UserLocationOverlayClassRef = useRef(null);
  const userLocationOverlayRef = useRef(null);

  const userLocation = propUserLocation || localUserLocation;

  useEffect(() => {
    if (mapsReady) return;
    const id = setInterval(() => {
      if (window.google && window.google.maps) {
        setMapsReady(true);
        clearInterval(id);
      }
    }, 100);
    return () => clearInterval(id);
  }, [mapsReady]);

  // Set the Moment locale based on 'language'
  useEffect(() => {
    const locale = setMomentLocale(language);
    moment.locale(locale);
  }, [language]);

  // Check geolocation permission status for the current-location control.
  useEffect(() => {
    const checkLocationPermission = async () => {
      if (!navigator.geolocation) {
        setLocationPermission('denied');
        return;
      }

      try {
        if (navigator.permissions) {
          const permission = await navigator.permissions.query({ name: 'geolocation' });
          setLocationPermission(permission.state);

          permission.addEventListener('change', () => {
            setLocationPermission(permission.state);
          });
        } else {
          setLocationPermission('prompt');
        }
      } catch (error) {
        console.warn('Could not check geolocation permission:', error);
        setLocationPermission('prompt');
      }
    };

    checkLocationPermission();
  }, []);

  // Process and group nearby events
  useEffect(() => {
    if (Array.isArray(places) && places.length > 0) {
      const grouped = groupNearbyEvents(places);
      setGroupedPlaces(grouped);
    } else {
      setGroupedPlaces([]);
    }
  }, [places]);

  // Create CustomOverlay class dynamically after Google Maps API is loaded
  useEffect(() => {
    if (mapApi && !CustomOverlayClassRef.current) {
      CustomOverlayClassRef.current = createCustomOverlayClass(mapApi);
    }
    // CRITICAL: Also create UserLocationOverlay class
    if (mapApi && !UserLocationOverlayClassRef.current) {
      UserLocationOverlayClassRef.current = createUserLocationOverlayClass(mapApi);
    }
  }, [mapApi]);

  // CRITICAL FIX: Manage user location overlay with Custom Overlay class
  useEffect(() => {
    if (mapInstance && UserLocationOverlayClassRef.current && userLocation) {
      const UserLocationOverlay = UserLocationOverlayClassRef.current;
      
      if (userLocationOverlayRef.current) {
        // Update existing overlay position
        userLocationOverlayRef.current.updatePosition(userLocation);
      } else {
        // Create new overlay
        const overlay = new UserLocationOverlay(userLocation, mapInstance);
        userLocationOverlayRef.current = overlay;
      }
    } else if (!userLocation && userLocationOverlayRef.current) {
      // Remove overlay if no user location
      userLocationOverlayRef.current.setMap(null);
      userLocationOverlayRef.current = null;
    }
  }, [mapInstance, userLocation]);

  // Helper utilities for ID-first selection
  const getIndexByPlaceId = useCallback((places, id) => {
    return places.findIndex(p => String(p.id) === String(id) || String(p.event_id) === String(id));
  }, []);

  const getSelectedPlace = useCallback(() => {
    if (selectedEventId === null || selectedEventId === undefined || !places.length) return null;

    const byIdIndex = getIndexByPlaceId(places, selectedEventId);
    if (byIdIndex !== -1) return places[byIdIndex];

    if (typeof selectedEventId === 'number' && places[selectedEventId]) {
      return places[selectedEventId];
    }

    return null;
  }, [selectedEventId, places, getIndexByPlaceId]);

  // Handle marker click for grouped events
  const handleGroupMarkerClick = useCallback((groupId) => {
    Object.entries(overlaysRef.current).forEach(([overlayGroupId, overlay]) => {
      if (overlayGroupId !== groupId && overlay?.setVisible) {
        overlay.setVisible(false);
      }
    });
  }, []);

  // Get search area button text based on language
  const getSearchAreaText = () => {
    switch (language) {
      case 'TH':
        return 'ค้นหาในพื้นที่นี้';
      case 'CN':
        return '搜索此区域';
      default:
        return 'Search This Area';
    }
  };

  const getCurrentLocationText = () => {
    switch (language) {
      case 'TH':
        return 'ตำแหน่งปัจจุบัน';
      case 'CN':
        return '当前位置';
      default:
        return 'Current Location';
    }
  };

  const getLocationDeniedText = () => {
    switch (language) {
      case 'TH':
        return 'โปรดอนุญาตการเปิดเผยตำแหน่งของคุณ';
      case 'CN':
        return '请允许位置访问';
      default:
        return 'Please allow location access';
    }
  };

  const handleCurrentLocationClick = () => {
    if (!navigator.geolocation) {
      return;
    }

    setLoadingCurrentLocation(true);

    const panToUserLocation = (position) => {
      const { latitude, longitude } = position.coords;
      const nextLocation = { lat: latitude, lng: longitude };

      setLocalUserLocation(nextLocation);
      setCoordinates(nextLocation);
      setLocationPermission('granted');

      if (mapInstance) {
        mapInstance.panTo(new window.google.maps.LatLng(latitude, longitude));
        mapInstance.setZoom(Math.max(mapInstance.getZoom() || 14, 16));
      }
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        panToUserLocation(position);
        setLoadingCurrentLocation(false);
      },
      (error) => {
        if (error.code !== error.TIMEOUT) {
          console.error('Error getting current location:', error);
        }

        if (error.code === error.TIMEOUT || error.code === error.POSITION_UNAVAILABLE) {
          navigator.geolocation.getCurrentPosition(
            (position) => {
              panToUserLocation(position);
              setLoadingCurrentLocation(false);
            },
            (finalError) => {
              if (finalError.code !== finalError.TIMEOUT) {
                console.error('High accuracy location failed:', finalError);
              }
              setLoadingCurrentLocation(false);

              if (finalError.code === finalError.PERMISSION_DENIED) {
                setLocationPermission('denied');
              }
            },
            {
              enableHighAccuracy: true,
              timeout: 30000,
              maximumAge: 0
            }
          );
        } else {
          setLoadingCurrentLocation(false);
          if (error.code === error.PERMISSION_DENIED) {
            setLocationPermission('denied');
          }
        }
      },
      {
        enableHighAccuracy: false,
        timeout: 10000,
        maximumAge: 60000
      }
    );
  };

  // Handle search area button click - searches events around current map center
  const handleSearchAreaClick = () => {
    if (!mapInstance) {
      return;
    }

    setLoadingLocation(true);

    const currentCenter = mapInstance.getCenter();
    const currentBounds = mapInstance.getBounds();

    if (currentCenter && currentBounds) {
      setCoordinates({
        lat: currentCenter.lat(),
        lng: currentCenter.lng()
      });

      const newBounds = {
        ne: { lat: currentBounds.getNorthEast().lat(), lng: currentBounds.getNorthEast().lng() },
        sw: { lat: currentBounds.getSouthWest().lat(), lng: currentBounds.getSouthWest().lng() },
        _src: 'user-search'
      };

      if (!areBoundsEqual(lastBoundsRef.current, newBounds)) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🗺️ Map: Setting bounds from "Search This Area" button with _src tag');
        }
        setBounds(newBounds);
        lastBoundsRef.current = newBounds;

        if (onSearch) {
          setTimeout(() => {
            onSearch('');
          }, 100);
        }
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.log('🗺️ Map: Skipping bounds update - identical to last bounds');
        }
        if (onSearch) {
          onSearch('');
        }
      }
    }

    setTimeout(() => {
      setLoadingLocation(false);
    }, 500);
  };

  // Create or update overlays when groupedPlaces changes
  useEffect(() => {
    if (mapInstance && mapApi && groupedPlaces.length > 0 && CustomOverlayClassRef.current) {
      const CustomOverlay = CustomOverlayClassRef.current;
      const currentOverlays = overlaysRef.current;
      const newOverlays = {};

      groupedPlaces.forEach((group) => {
        if (
          group &&
          group.id &&
          typeof group.latitude === 'number' &&
          typeof group.longitude === 'number'
        ) {
          const pos = { lat: group.latitude, lng: group.longitude };
          const existingOverlay = currentOverlays[group.id];

          if (existingOverlay) {
            const selectedPlace = getSelectedPlace();
            const currentSelectedEventId = selectedPlace?.id || null;

            existingOverlay.language = language;
            existingOverlay.groupData = group;
            existingOverlay.selectedEventId = currentSelectedEventId;
            existingOverlay.disablePopups = disablePopups;
            existingOverlay.onInfoBoxClick = onInfoBoxClick;
            existingOverlay.onCreatorClick = onCreatorClick;
            existingOverlay.renderOverlay();
            newOverlays[group.id] = existingOverlay;
            delete currentOverlays[group.id];
          } else {
            let initialVisible = false;
            const selectedPlace = getSelectedPlace();
            if (selectedPlace) {
              initialVisible = group.places.some(place => place.id === selectedPlace.id);
            }

            const currentSelectedEventId = selectedPlace?.id || null;

            const overlay = new CustomOverlay(
              pos,
              group,
              mapInstance,
              initialVisible,
              handleGroupMarkerClick,
              language,
              currentSelectedEventId,
              disablePopups,
              onInfoBoxClick,
              onCreatorClick
            );
            newOverlays[group.id] = overlay;
          }
        }
      });

      Object.values(currentOverlays).forEach((overlay) => {
        if (overlay?.setMap) overlay.setMap(null);
      });

      overlaysRef.current = newOverlays;
    }
  }, [groupedPlaces, mapInstance, mapApi, language, selectedEventId, places, handleGroupMarkerClick, getSelectedPlace, disablePopups, onInfoBoxClick, onCreatorClick]);

  // Update overlay properties when language or disablePopups changes
  useEffect(() => {
    Object.values(overlaysRef.current).forEach((overlay) => {
      if (overlay) {
        overlay.language = language;
        overlay.disablePopups = disablePopups;
        if (overlay.renderOverlay) {
          overlay.renderOverlay();
        }
      }
    });
  }, [language, disablePopups]);

  // Update overlay visibility when selectedEventId changes
  useEffect(() => {
    if (selectedEventId !== null) {
      const selectedPlace = getSelectedPlace();
      if (selectedPlace) {
        if (process.env.NODE_ENV === 'development') {
          console.log('🗺️ Map: Showing overlay for selected place:', selectedPlace.id);
        }
        Object.entries(overlaysRef.current).forEach(([groupId, overlay]) => {
          if (overlay?.setVisible) {
            const hasSelectedPlace = overlay.groupData.places.some(
              place => place.id === selectedPlace.id
            );
            overlay.setVisible(hasSelectedPlace);
            if (hasSelectedPlace && process.env.NODE_ENV === 'development') {
              console.log('🗺️ Map: Group', groupId, 'contains selected place');
            }
          }
        });
      }
    } else {
      if (process.env.NODE_ENV === 'development') {
        console.log('🗺️ Map: Hiding all overlays (no selection)');
      }
      Object.values(overlaysRef.current).forEach(overlay => {
        if (overlay?.setVisible) overlay.setVisible(false);
      });
    }
  }, [selectedEventId, places, getSelectedPlace]);

  // Re-center map if coordinates change significantly
  useEffect(() => {
    if (mapInstance && coordinates?.lat && coordinates?.lng) {
      const currentCenter = mapInstance.getCenter();
      if (
        currentCenter &&
        (Math.abs(currentCenter.lat() - coordinates.lat) > 0.0001 ||
          Math.abs(currentCenter.lng() - coordinates.lng) > 0.0001)
      ) {
        mapInstance.panTo(new window.google.maps.LatLng(coordinates.lat, coordinates.lng));
      }
    }
  }, [coordinates, mapInstance]);

  // Cleanup overlays on unmount
  useEffect(() => {
    return () => {
      Object.values(overlaysRef.current).forEach((overlay) => {
        if (overlay?.setMap) overlay.setMap(null);
      });
      overlaysRef.current = {};
      
      // CRITICAL: Also cleanup user location overlay
      if (userLocationOverlayRef.current) {
        userLocationOverlayRef.current.setMap(null);
        userLocationOverlayRef.current = null;
      }
    };
  }, []);

  // Responsive button sizing
  const isMobile = window.innerWidth <= 768;
  const buttonSize = isMobile ? 36 : 44;
  const buttonRadius = isMobile ? 10 : 12;
  const iconSize = isMobile ? 18 : 22;
  const effectiveControlsBottomOffset = controlsBottomOffset ?? (isMobile ? 330 : 20);
  const searchAreaBottom = effectiveControlsBottomOffset + buttonSize + 10;
  const isCurrentLocationAvailable = Boolean(navigator.geolocation);

  if (!mapsReady) {
    return <div style={{ width: '100%', height: '100%', background: '#e8eaed' }} />;
  }

  return (
    <div style={{ height: '100%', width: '100%', position: 'relative' }}>
      <GoogleMapReact
        bootstrapURLKeys={{ key: process.env.REACT_APP_Maps_API_KEY }}
        center={coordinates}
        zoom={14}
        options={{
          styles: mapStyles,
          disableDefaultUI: true,
          zoomControl: true,
          gestureHandling: 'greedy',
          clickableIcons: false,
          tilt: 0,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
        }}
        yesIWantToUseGoogleMapApiInternals
        onGoogleApiLoaded={({ map, maps }) => {
          setMapInstance(map);
          setMapApi(maps);
          if (map) {
            map.setOptions({
              draggableCursor: 'grab',
              draggingCursor: 'grabbing',
              zoomControlOptions: { position: maps.ControlPosition.RIGHT_BOTTOM }
            });
            map.addListener('click', () => {
              if (onMarkerClick) {
                if (process.env.NODE_ENV === 'development') {
                  console.log('🗺️ Map background clicked - clearing selection');
                }
                onMarkerClick(null);
              }
            });

            if (maps?.event && !initialBoundsSetRef.current && !propBounds) {
              if (process.env.NODE_ENV === 'development') {
                console.log('🗺️ Map: Setting up initial bounds listener (parent has no bounds)');
              }
              maps.event.addListenerOnce(map, 'idle', () => {
                if (!initialBoundsSetRef.current && map.getBounds() && !propBounds) {
                  const b = map.getBounds();
                  const newBounds = {
                    ne: { lat: b.getNorthEast().lat(), lng: b.getNorthEast().lng() },
                    sw: { lat: b.getSouthWest().lat(), lng: b.getSouthWest().lng() }
                  };

                  if (!areBoundsEqual(lastBoundsRef.current, newBounds)) {
                    if (process.env.NODE_ENV === 'development') {
                      console.log('🗺️ Map: Setting initial bounds');
                    }
                    setBounds(newBounds);
                    lastBoundsRef.current = newBounds;
                  }
                  initialBoundsSetRef.current = true;
                }
              });
            }
          }
        }}
        onChange={(e) => {
          if (e && e.center && e.marginBounds) {
            const newCoords = { lat: e.center.lat, lng: e.center.lng };
            const newBounds = { ne: e.marginBounds.ne, sw: e.marginBounds.sw };

            // CRITICAL: Update coordinates when user pans manually
            // This keeps the center prop in sync with the map's actual center
            if (!areBoundsEqual(lastBoundsRef.current, newBounds)) {
              setCoordinates(newCoords);
              setBounds(newBounds);
              lastBoundsRef.current = newBounds;
            }
          }
        }}
      >
        {/* REMOVED: No longer using React component for user location marker */}
        {/* User location is now handled by Custom Overlay class */}
      </GoogleMapReact>

      {/* Current Location Button */}
      <button
        onClick={handleCurrentLocationClick}
        disabled={!isCurrentLocationAvailable || loadingCurrentLocation}
        title={isCurrentLocationAvailable ? getCurrentLocationText() : getLocationDeniedText()}
        className="map-control-btn"
        style={{
          bottom: `${effectiveControlsBottomOffset}px`,
        }}
      >
        {loadingCurrentLocation ? (
          <div
            style={{
              width: '20px',
              height: '20px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderTop: '2px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
        ) : (
          <svg width={iconSize} height={iconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="3" fill="currentColor" />
            <line x1="12" y1="1" x2="12" y2="4" />
            <line x1="12" y1="20" x2="12" y2="23" />
            <line x1="1" y1="12" x2="4" y2="12" />
            <line x1="20" y1="12" x2="23" y2="12" />
          </svg>
        )}
      </button>

      {/* Search Area Button */}
      <button
        onClick={handleSearchAreaClick}
        disabled={loadingLocation}
        title={getSearchAreaText()}
        className="map-control-btn"
        style={{
          bottom: `${searchAreaBottom}px`,
        }}
      >
        {loadingLocation ? (
          <div
            style={{
              width: '20px',
              height: '20px',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              borderTop: '2px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
            }}
          />
        ) : (
          <svg
            width={iconSize}
            height={iconSize}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              color: 'white',
            }}
          >
            <path
              d="M3 12C3 16.9706 7.02944 21 12 21C16.9706 21 21 16.9706 21 12C21 7.02944 16.9706 3 12 3C9.5 3 7.26 4.04 5.64 5.64"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
            <path
              d="M9 5L5.64 5.64L6.28 9"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </svg>
        )}
      </button>

      {/* CSS Animations and Styles */}
      <style>{`
        .map-control-btn {
          position: absolute;
          right: ${isMobile ? '12px' : '20px'};
          width: ${buttonSize}px;
          height: ${buttonSize}px;
          border-radius: 50%;
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 8px 24px rgba(29, 78, 216, 0.3), 0 2px 6px rgba(0, 0, 0, 0.15);
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
          outline: none;
        }
        
        .map-control-btn:hover:not(:disabled) {
          transform: translateY(-2px) scale(1.05);
          background: linear-gradient(135deg, #60a5fa 0%, #2563eb 100%);
          box-shadow: 0 12px 28px rgba(37, 99, 235, 0.4), 0 4px 10px rgba(0, 0, 0, 0.2);
        }
        
        .map-control-btn:active:not(:disabled) {
          transform: translateY(0px) scale(0.95);
          box-shadow: 0 4px 12px rgba(29, 78, 216, 0.2);
        }
        
        .map-control-btn:disabled {
          background: #9ca3af;
          border-color: rgba(0, 0, 0, 0.05);
          box-shadow: none;
          cursor: not-allowed;
          opacity: 0.7;
        }

        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        @keyframes pulse {
          0% { opacity: 1; }
          50% { opacity: 0.7; }
          100% { opacity: 1; }
        }
        
        @keyframes userLocationPulse {
          0% {
            transform: translate(-50%, -50%) scale(1);
            opacity: 1;
          }
          50% {
            transform: translate(-50%, -50%) scale(1.5);
            opacity: 0.5;
          }
          100% {
            transform: translate(-50%, -50%) scale(2);
            opacity: 0;
          }
        }
      `}</style>

    </div>
  );
};

export default Map;
